import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { IProduct } from "redux/reducers/models/product";
import Checkbox from "components/form/checkbox/Checkbox";
import ActionBar from "components/actionBar/ActionBar";
import DataTable from "../../../../components/dataTable/DataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/pro-light-svg-icons";
import { Badge } from "components/badge/Badge";
import { StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import { GetKitchenLocations, TGetKitchenLocation } from "api/rpc/2024-04/facilityAdmin/facility/kitchen/location";
import { TKitchenLocation } from "redux/reducers/models/kitchen";
import { GetAllSalesChannels } from "api/rpc/2024-04/clientAdmin/facility/salesChannel";
import { LocaleCurrency } from "helpers/Locale";

type ProductTableProps = {
  /** Adds 2 columns to Products Table.
   *  - Enables attach/detach functionality with table
   */
  type: "facility";
  products: IProduct[];
  attachProductToFacility: (event: React.MouseEvent<HTMLElement, MouseEvent>, productId: number) => void;
  tableLimit: number;
  tableOffset: number;
  handleTableOffset: (direction: "prev" | "next") => void;
  removeProducts: (productIds: Array<number>) => void;
};

type ClientProductTableProps = {
  /** Only Name | Type & Variants columns are displayed.
   *  - Client cannot attach/detach facility products
   */
  type: "client";
  products: IProduct[];
  attachProductToFacility?: never; // Client cannot attach/detach a product
  tableLimit: number;
  tableOffset: number;
  handleTableOffset: (direction: "prev" | "next") => void;
  removeProducts: (productIds: Array<number>) => void;
};

export default function ProductsTable(props: ProductTableProps | ClientProductTableProps) {
  const history = useHistory();
  const { t } = useTranslation();
  const [checkboxState, setCheckboxState] = useState<number[]>([]);
  const checkedProducts = props?.products?.filter(product => checkboxState.includes(product.id) && product) || [];

  const [locationInfo, setLocationInfo] = useState<TKitchenLocation[]>([]); // TODO: Define the type for locationInfo
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  /** @param [parent=false] Optional param to toggle every checkbox. */
  function handleChangeEvent(event: React.ChangeEvent<HTMLInputElement>, parent = false) {
    const targetId = Number(event.target.id);

    // Title checkbox click.  Return early.
    if (parent) {
      if (!event.target.checked) {
        setCheckboxState([]);
      } else {
        const allFacilityProducts = props.products
          ?.filter(product => product.facility_access)
          ?.map(product => product.id);
        const allClientProducts = props.products?.map(product => product.id);
        setCheckboxState(props.type === "facility" ? allFacilityProducts : allClientProducts);
      }
      return;
    }

    // Table Row checkbox click
    const updatedState: Array<number> = [...checkboxState];
    const foundIndex = checkboxState.indexOf(targetId);

    if (foundIndex > -1) {
      updatedState.splice(foundIndex, 1);
    } else {
      updatedState.push(targetId);
    }

    setCheckboxState(updatedState);
  }

  // Determines items needing removal.  Reset checkboxState.
  function handleProductRemoval() {
    if (checkedProducts.map(product => product.id).length > 0) {
      props.removeProducts(checkedProducts.map(product => product.id));
      setCheckboxState([]);
    }
  }

  async function loadFacilityProductInfo() {
    const params3: TGetKitchenLocation = {};
    const locationRes = await GetKitchenLocations(params3, false);

    if (locationRes.status !== StatusCode.OK) {
      showError("Unable to load product information");
      return;
    }

    setLocationInfo(Array.isArray(locationRes.data) ? locationRes.data : [locationRes.data]);
    console.log("Products", props);
  }

  async function loadClientProductInfo() {
    const availabilityRes = await GetAllSalesChannels();

    if (availabilityRes.status !== StatusCode.OK) {
      showError("Unable to load product information");
      return;
    }
  }

  // Load product information when component mounts
  useEffect(() => {
    if (!isDataLoaded) {
      if (props.type === "facility") {
        void loadFacilityProductInfo().then(() => setIsDataLoaded(true));
      } else if (props.type === "client") {
        void loadClientProductInfo().then(() => setIsDataLoaded(true));
      }
    }
  }, [isDataLoaded, props.type]);

  function handleInStock(product: IProduct) {
    // console.log("Checking in stock for product:", product);
    for (const variant of product.variants) {
      if (
        (variant.inventory_units && variant.inventory_units > 0) ||
        variant.inventory_item?.inventory_level?.quantity_available > 0
      ) {
        return true;
      }
    }
    return false;
  }

  return (
    <ActionBar
      visible={checkboxState?.length > 0}
      selectedItemIds={checkboxState}
      clearSelectedItemIds={() => setCheckboxState([])}
      itemSingularTitle={t("Product")}
      itemPluralTitle={t("Products")}
      primaryActions={[
        {
          icon: faTrash,
          title:
            props.type === "facility"
              ? t("Remove Products")
              : t("Archive Products"),
          action: handleProductRemoval,
        },
      ]}
    >
      <DataTable
        columns={
          props.type === "facility"
            ? [
              {
                label: "",
                content: (
                  <span className="flex flex-row" style={{ fontWeight: "normal", fontSize: "12px" }}>
                    <Checkbox
                      size="small"
                      checked={
                        props.products?.length !== 0 &&
                        props.type === "facility" &&
                        checkboxState.length === props.products?.filter(val => val.facility_access).length
                      }
                      onChange={event => handleChangeEvent(event, true)}
                    />
                    {t("secure.facility.product.product_table.001")}
                  </span>
                ),
              },
              { label: t("secure.facility.product.product_table.002") },
              { label: t("secure.facility.product.product_table.003") },
              { label: "Availability" },
              { label: "Location" },
              { label: "SKU" },
              { label: "Price" },
              { label: "Inventory" },
              { label: "" },
            ]
            : [
              {
                label: "",
                content: (
                  <span className="flex flex-row" style={{ fontWeight: "normal", fontSize: "12px" }}>
                    <Checkbox
                      size="small"
                      checked={
                        props.products?.length !== 0 &&
                        props.type === "client" &&
                        checkboxState.length === props.products?.length
                      }
                      onChange={event => handleChangeEvent(event, true)}
                    />
                    {t("secure.facility.product.product_table.001")}
                  </span>
                ),
              },
              { label: t("secure.facility.product.product_table.002") },
              { label: t("secure.facility.product.product_table.003") },
              // { label: "Availability" },
              { label: "SKU" },
              { label: "Price" },
              { label: "Inventory" },
              { label: "" },
            ]
        }
        stickyHeader
        footer={{
          tableLimit: props.tableLimit,
          tableOffset: props.tableOffset,
          handleTableOffset: direction => props.handleTableOffset(direction),
          disableNextOffset: !props.products || !(props.products?.length === props.tableLimit),
        }}
        loading={props.products === undefined}
        className="products-table"
      >
        {isDataLoaded &&
          props.products?.map((product, productIndex) => {
            return (
              <tr
                key={product.id || `product-${productIndex}`}
                className="clickable"
                onClick={() => history.push("/admin/product/" + String(product.id))}
              >
                <td className="table-data-checkbox" key={`checkbox-${product.id || productIndex}`}>
                  <span className="flex flex-row" style={{ fontWeight: "normal", fontSize: "12px" }}>
                    <div onClick={event => event.stopPropagation()}>
                      <Checkbox
                        id={String(product.id)}
                        size="small"
                        checked={checkboxState.includes(product.id) ?? false}
                        onChange={event => handleChangeEvent(event)}
                        disabled={props.type === "facility" && !product?.facility_access}
                      />
                    </div>
                    <div style={{ width: "100%" }} onClick={() => history.push("/admin/product/" + String(product.id))}>
                      <p className="table-cell-lead-text">{product.title}</p>
                      <p className="table-cell-support-text">{product.subtitle}</p>
                    </div>
                  </span>
                </td>
                <td className="table-data-types justify-between" key={`type-${product.id}`}>
                  {product.type}
                </td>
                <td className="table-data-variants" key={`variants-${productIndex}`}>
                  {product.variant_count === 1
                    ? `${product.variant_count} Variant`
                    : `${product.variant_count} Variants`}
                </td>
                {props.type === "facility" ? (
                  <td className="table-data-inventory" key={`availability-${productIndex}`}>
                    {/* Cannot attach to facility if it cannot be sold */}
                    {product?.cannot_be_sold ? (
                      <Badge type="error" size="small">
                        {t("secure.facility.product.product_table.005")}
                      </Badge>
                    ) : (
                      <>
                        {product?.sales_channels?.length > 0 ? (
                          <div key={`sales-channels-${productIndex}-${product.id}`}>
                            {product?.sales_channels?.map(channel => {
                              return (
                                <div key={`sales-channel-${channel.id}-${product.id}-${productIndex}`}>
                                  {channel.title === "Point of Sale" ? (
                                    <div
                                      className="product-inventory-level-table-quantity"
                                      style={{ color: "#3D5FA0", backgroundColor: "#CDD7EC", padding: "auto" }}
                                      key={`sales-channel-${channel.id}-${productIndex}`}
                                    >
                                      {t('In-Store')}
                                    </div>
                                  ) : null}
                                  {channel.title === "Online Store" ? (
                                    <>
                                      <div
                                        className="product-inventory-level-table-quantity"
                                        style={{ color: "#5925DC", backgroundColor: "#F4F3FF", marginTop: "5px" }}
                                        key={`sales-channel-${channel.id}-${productIndex}`}
                                      >
                                        {t('Online')}
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ justifySelf: "center" }}>-</div>
                        )}
                      </>
                    )}
                  </td>
                ) : null}

                {props.type === "facility" ? (
                  <td key={`kitchen-location-${productIndex}`}>
                    {product?.kitchen_location_id ? (
                      <div className="flex gap-1" style={{ justifySelf: "center" }}>
                        {locationInfo.find(location => location.id === product?.kitchen_location_id)?.title ?? ""}
                        {product.meta?.kitchen_location_id_2 || product.meta?.kitchen_location_id_3 ? (
                          <>
                            {product.meta?.kitchen_location_id_2 && product.meta?.kitchen_location_id_3 ? (
                              <Badge type="gray">+2</Badge>
                            ) : (
                              <Badge type="gray">+1</Badge>
                            )}
                          </>
                        ) : null}
                      </div>
                    ) : (
                      <div style={{ justifySelf: "center", alignSelf: "center" }}>-</div>
                    )}
                  </td>
                ) : null}

                <td key={`SKU-${product.id}`}>
                  {product.variants[0]?.sku ?? <div style={{ justifySelf: "center" }}>-</div>}
                </td>
                <td key={`Price-${product.id}`}>
                  {product.variants[0]?.price ? (
                    <>
                      {product.variants.length > 1 ? (
                        <>
                          <LocaleCurrency
                            amount={
                              product.variants.reduce((prev, curr) => {
                                return prev.price < curr.cost ? prev : curr;
                              }).price
                            }
                          />
                          {"+"}
                        </>
                      ) : (
                        <LocaleCurrency amount={product.variants[0].price} />
                      )}
                    </>
                  ) : (
                    <div style={{ justifySelf: "center" }}>-</div>
                  )}
                </td>
                <td key={`Units-${product.id}`}>
                  {props.type === "facility" && !product?.facility_access ? (
                    <div className="inventory-badge">
                      <Badge
                        onClick={event => {
                          setCheckboxState([]);
                          props.attachProductToFacility(event, product.id);
                        }}
                        type="warning"
                        size="small"
                      >
                        {t("secure.facility.product.product_table.006")}
                      </Badge>
                    </div>
                  ) : (
                    <div style={{ justifySelf: "center" }}>{handleInStock(product) ? "In Stock" : "Out of Stock"}</div>
                  )}
                </td>
                <td key={`edit-${product.id}`}>
                  <FontAwesomeIcon icon={faPen} />
                </td>
              </tr>
            );
          })}
      </DataTable>
    </ActionBar>
  );
}
